import qs from 'query-string';
import { removeEmptyAndDefaultFilters } from 'services/contacts/filters';
import { encodeToken, decodeToken } from 'services/encrypt';

export const getFilters = ({ search }) => {
  const { filters } = qs.parse(search, { arrayFormat: 'bracket' });
  let filterObject;
  try {
    filterObject = JSON.parse(filters);
  } catch (e) {
    filterObject = {};
  }

  return filterObject;
};

export const qsParse = ({ search }) => qs.parse(search, { decode: true, arrayFormat: 'bracket' });

export const qsStringify = (params) => qs.stringify(params, { encode: true, arrayFormat: 'bracket' });

/* eslint-disable no-param-reassign */
const exclude = new Set(['contactId', 'companyIds', 'aiFilters']);

// Helper function to check if key is simple (no brackets or dots)
const isSimpleKey = (key) => /^\w+$/.test(key);

// Using your existing decodeValue function
const decodeValue = (item) => {
  const value = safelyDecodeURIComponent(item);

  if (value === '') return '';

  if (value === 'null') {
    return null;
  }

  if (value === 'undefined' || typeof value === 'undefined') {
    return undefined;
  }

  if (value === 'false') return false;

  if (value === 'true') return true;

  if (!Number.isNaN(Number(value))) {
    return Number(value);
  }

  return value;
};

// Main parser for complex keys
const parseComplexKey = (key, value, filters) => {
  // Pattern: array[index].object.property (e.g., users[0].profile.name)
  const arrayObjectPropertyMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)\.(\w+)$/);
  if (arrayObjectPropertyMatch) {
    parseArrayObjectProperty(arrayObjectPropertyMatch, value, filters);
    return;
  }

  // Pattern: array[index].property[index] (e.g., sources[0].value[0])
  const nestedArrayMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)\[(\d+)\]$/);
  if (nestedArrayMatch) {
    parseNestedArray(nestedArrayMatch, value, filters);
    return;
  }

  // Pattern: array[index].property (e.g., sources[0].label)
  const arrayObjectMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
  if (arrayObjectMatch) {
    parseArrayObject(arrayObjectMatch, value, filters);
    return;
  }

  // Pattern: array[index] (e.g., tags[0])
  const simpleArrayMatch = key.match(/^(\w+)\[(\d+)\]$/);
  if (simpleArrayMatch) {
    parseSimpleArray(simpleArrayMatch, value, filters);
    return;
  }

  // Pattern: multi-level object properties (e.g., companyNamePeriod.fromMonth.label)
  const multiLevelObjectMatch = key.match(/^([^.[\]]+(?:\.[^.[\]]+)+)$/);
  if (multiLevelObjectMatch) {
    parseMultiLevelObject(key, value, filters);
    return;
  }

  // Pattern: object.property (e.g., user.name)
  const simpleObjectMatch = key.match(/^([^.[\]]+)\.([^.\\[\]]+)$/);
  if (simpleObjectMatch) {
    parseSimpleObject(simpleObjectMatch, value, filters);
  }
};

// Parse arrays with nested objects: users[0].profile.name=Alice
const parseArrayObjectProperty = ([, arrayName, arrayIndex, objectName, propertyName], value, filters) => {
  const arrIdx = parseInt(arrayIndex, 10);

  // Initialize array
  if (!filters[arrayName]) {
    filters[arrayName] = [];
  }

  // Initialize object at array index
  if (!filters[arrayName][arrIdx]) {
    filters[arrayName][arrIdx] = {};
  }

  // Initialize nested object
  if (!filters[arrayName][arrIdx][objectName]) {
    filters[arrayName][arrIdx][objectName] = {};
  }

  // Set property value in nested object
  filters[arrayName][arrIdx][objectName][propertyName] = decodeValue(value);
};

// Parse nested arrays: sources[0].value[0]=shared_list
const parseNestedArray = ([, arrayName, arrayIndex, propertyName, propertyIndex], value, filters) => {
  const arrIdx = parseInt(arrayIndex, 10);
  const propIdx = parseInt(propertyIndex, 10);

  // Initialize array
  if (!filters[arrayName]) {
    filters[arrayName] = [];
  }

  // Initialize object at array index
  if (!filters[arrayName][arrIdx]) {
    filters[arrayName][arrIdx] = {};
  }

  // Initialize nested array property
  if (!filters[arrayName][arrIdx][propertyName]) {
    filters[arrayName][arrIdx][propertyName] = [];
  }

  // Set value at nested array index
  filters[arrayName][arrIdx][propertyName][propIdx] = decodeValue(value);
};

// Parse array of objects: sources[0].label=Manual
const parseArrayObject = ([, arrayName, arrayIndex, propertyName], value, filters) => {
  const arrIdx = parseInt(arrayIndex, 10);

  // Initialize array
  if (!filters[arrayName]) {
    filters[arrayName] = [];
  }

  // Initialize object at array index
  if (!filters[arrayName][arrIdx]) {
    filters[arrayName][arrIdx] = {};
  }

  // Set property value
  filters[arrayName][arrIdx][propertyName] = decodeValue(value);
};

// Parse simple arrays: tags[0]=javascript
const parseSimpleArray = ([, arrayName, arrayIndex], value, filters) => {
  const arrIdx = parseInt(arrayIndex, 10);

  // Initialize array
  if (!filters[arrayName]) {
    filters[arrayName] = [];
  }

  // Set value at index
  filters[arrayName][arrIdx] = decodeValue(value);
};

// Parse multi-level objects: companyNamePeriod.fromMonth.label=January
const parseMultiLevelObject = (key, value, filters) => {
  const parts = key.split('.');
  let current = filters;

  // Navigate/create the nested structure
  for (let i = 0; i < parts.length - 1; i += 1) {
    const part = decodeURIComponent(parts[i]);
    if (!current[part]) {
      current[part] = {};
    }
    current = current[part];
  }

  // Set the final value
  const finalKey = decodeURIComponent(parts[parts.length - 1]);
  current[finalKey] = decodeValue(value);
};

// Parse simple objects: user.name=john
const parseSimpleObject = ([, objectName, propertyName], value, filters) => {
  // Decode the object and property names since they might be URL encoded
  const decodedObjectName = decodeURIComponent(objectName);
  const decodedPropertyName = decodeURIComponent(propertyName);

  // Initialize object
  if (!filters[decodedObjectName]) {
    filters[decodedObjectName] = {};
  }

  // Set property value
  filters[decodedObjectName][decodedPropertyName] = decodeValue(value);
};

const safelyDecodeURIComponent = (item) => {
  try {
    return decodeURIComponent(item);
  } catch (e) {
    return item;
  }
};

export const convertStringBackToQuery = (queryString) => {
  const query = {};
  const filters = {};

  if (!queryString) return { ...query, filters };

  const queryParams = queryString
    .replace(/^\?/, '')
    .split('&')
    .map((param) => decodeURIComponent(param));

  queryParams.forEach((param) => {
    if (!param) return;

    const [encodedKey, encodedValue] = param.split('=');
    const key = safelyDecodeURIComponent(encodedKey || '');
    const value = safelyDecodeURIComponent(encodedValue || '');

    // Handle new compressed filters format
    if (key === 'filters' && value) {
      try {
        const decodedFilters = decodeToken(value);
        Object.assign(filters, decodedFilters);
        return;
      } catch (e) {
        // If decode fails, fall through to old parsing logic
      }
    }

    // Simple key-value pairs (no brackets or dots)
    if (isSimpleKey(key)) {
      query[key] = decodeValue(value);
      return;
    }

    // Parse complex keys and add to filters
    parseComplexKey(key, value, filters);
  });

  return { ...query, filters };
};

export const convertQueryToString = ({ filters, ...query }, defaultsFromUrl = '') => {
  const result = new Map();

  const encodeQuery = (key, value) => `${key}=${encodeURIComponent(value)}`;

  Object.keys(query).forEach((key) => {
    if (query[key] === undefined || query[key] === null || query[key] === '') return;
    result.set(key, encodeQuery(key, query[key]));
  });

  const includedFilters = Object.keys(filters).reduce((acc, key) => {
    if (!exclude.has(key)) {
      acc[key] = filters[key];
    }
    return acc;
  }, {});
  const cleanedFilters = removeEmptyAndDefaultFilters(includedFilters);

  // Remove UI-only properties that shouldn't be in the URL
  // These are used for tracking/display but create issues when encoded
  const sanitizeFilterItem = (item) => {
    if (!item || typeof item !== 'object') return item;
    if (Array.isArray(item)) return item.map(sanitizeFilterItem);

    const { key, ...rest } = item; // Remove 'key' property
    return Object.keys(rest).reduce((acc, k) => {
      acc[k] = sanitizeFilterItem(rest[k]);
      return acc;
    }, {});
  };

  const sanitizedFilters = Object.keys(cleanedFilters).reduce((acc, key) => {
    acc[key] = sanitizeFilterItem(cleanedFilters[key]);
    return acc;
  }, {});

  const encodedFilters = encodeToken(sanitizedFilters);

  result.set('filters', encodeQuery('filters', encodedFilters));

  return `${Array.from(result.values()).join('&')}${defaultsFromUrl ? `&${defaultsFromUrl}` : ''}`;
};
